"use client";
import React, { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useEventDetailsPageStore } from "~/state/store";
import { If } from "../Conditional";
import { Label } from "../shadcn/Label";
import { RiCloseFill } from "react-icons/ri";
import { cn } from "~/utils/fontsConfig";
import { EventFormData } from "./EventViewAndEdit/EventFormDataType";
import { BASE_INPUT_STYLES } from "../themed/BASE_STYLE_DEFINITIONS";
import Link from "next/link";
import { EventBySlugOutput } from "~/types/EventTypesInferred";

const EventTags = ({
    event,
    tagType,
    children,
}: {
    event: EventBySlugOutput;
    tagType: "tags" | "resourceTags";
    children?: React.ReactNode;
}) => {
    const form = useFormContext<EventFormData>();
    const { panelMode } = useEventDetailsPageStore();

    const tags = form.watch(tagType) || [];
    const [newTag, setNewTag] = useState("");

    // Only when hitting enter
    const handleAddTag = () => {
        const sanitizedTag = newTag.trim();
        if (sanitizedTag === "") return;
        if (tags.includes(sanitizedTag)) return;
        addTag(sanitizedTag);
    };

    const addTag = (newTags: string[] | string) => {
        const updatedTags = [...tags];
        const newInputs = Array.isArray(newTags) ? newTags : [newTags];

        for (const tag of newInputs) {
            if (tags.includes(tag)) continue;
            updatedTags.push(tag);
        }
        form.setValue(tagType, updatedTags, { shouldDirty: true });
        setNewTag("");
    };
    // This handles single and multiple tags at once (like when pasting)
    const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newInputs = e.target.value
            .split(/,/)
            .filter((v) => v.trim() !== "");

        if (newInputs.length === 0) {
            setNewTag(e.target.value);
            return;
        }

        // Single tag
        if (newInputs.length === 1) {
            const value = e.target.value;
            const lastChar = value.slice(-1);
            if (lastChar !== ",") {
                setNewTag(value);
            } else {
                addTag(newInputs);
            }
            return;
        }

        // Multiple tags
        if (newInputs.length >= 1) {
            addTag(newInputs);
        }
    };

    if (tagType === "resourceTags" && panelMode === "default") return null;

    return (
        <div className="w-full">
            <If condition={panelMode === "edit"}>
                <Label>
                    {tagType === "resourceTags" ? "Resource Tags" : "Tags"}
                </Label>
                <div
                    className={cn(
                        BASE_INPUT_STYLES,
                        "flex flex-wrap gap-2 p-2",
                    )}
                >
                    {tags.map((tag) => (
                        <EventTag
                            key={tag + Math.random()}
                            tag={tag}
                            isEditing={true}
                            onRemoveTag={(tag) => {
                                const updatedTags = tags.filter(
                                    (t) => t !== tag,
                                );
                                form.setValue(tagType, updatedTags, {
                                    shouldDirty: true,
                                });
                            }}
                        />
                    ))}
                    <input
                        placeholder="Add tag..."
                        value={newTag}
                        onChange={handleChangeInput}
                        className="min-w-[100px] flex-grow border-none bg-transparent focus:outline-none focus:ring-0"
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddTag();
                            }
                        }}
                    />
                </div>
            </If>

            <If condition={panelMode === "default" && tagType === "tags"}>
                <div className="flex flex-wrap items-center gap-2">
                    {tags.map((tag) => (
                        <EventTag
                            key={tag}
                            tag={tag}
                            isEditing={false}
                            communitySlug={event.community?.slug || ""}
                        />
                    ))}
                    {children}
                </div>
            </If>
        </div>
    );
};

export const EventTag = ({
    tag,
    isEditing,
    className,
    onRemoveTag,
    communitySlug,
}: {
    tag: string;
    isEditing: boolean;
    className?: string;
    onRemoveTag?: (tag: string) => void;
    communitySlug?: string;
}) => {
    return (
        <div
            className={cn(
                "flex h-[30px] items-center rounded-lg bg-v3-N-400 px-2",
                "text-sm",
                className,
            )}
        >
            {communitySlug ? (
                <Link
                    href={`/${communitySlug}/tags/${encodeURIComponent(tag)}`}
                    className="hover:underline"
                >
                    {tag}
                </Link>
            ) : (
                <span>{tag}</span>
            )}
            {isEditing && (
                <button
                    onClick={() => onRemoveTag?.(tag)}
                    className="ml-1 p-1 hover:text-v3-N-100"
                    aria-label={`Remove tag ${tag}`}
                >
                    <RiCloseFill />
                </button>
            )}
        </div>
    );
};

export default EventTags;
